/**
 * useSummonAurelius Hook
 *
 * Custom hook for navigating to Marcus AI (Insights AI Assistant) with pre-populated message
 * about specific assets, vulnerabilities, or technologies.
 *
 * Usage:
 * ```tsx
 * const { summonAurelius } = useSummonAurelius();
 * summonAurelius({
 *   type: 'asset',
 *   keys: ['asset-key-1'],
 *   displayNames: ['example.com']
 * });
 * ```
 *
 * @module hooks
 */

import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';

export interface SummonEntity {
  /** Entity type being analyzed */
  type: 'asset' | 'vulnerability' | 'technology' | 'seed';
  /** Entity keys (limited to 10) */
  keys: string[];
  /** Display names for entities (limited to 10) */
  displayNames: string[];
}

export interface UseSummonAureliusReturn {
  /** Navigate to Marcus AI with entity context */
  summonAurelius: (entities: SummonEntity) => void;
}

/**
 * useSummonAurelius Hook
 *
 * Provides navigation to Marcus AI Assistant with entity-specific message.
 * Automatically limits to 10 entities and formats appropriate message.
 *
 * @returns Hook interface with summonAurelius function
 *
 * @example
 * ```tsx
 * const { summonAurelius } = useSummonAurelius();
 *
 * // Single entity
 * summonAurelius({
 *   type: 'vulnerability',
 *   keys: ['vuln-key-1'],
 *   displayNames: ['CVE-2024-1234']
 * });
 *
 * // Multiple entities
 * summonAurelius({
 *   type: 'asset',
 *   keys: ['key1', 'key2', 'key3'],
 *   displayNames: ['asset1.com', 'asset2.com', 'asset3.com']
 * });
 * ```
 */
export function useSummonAurelius(): UseSummonAureliusReturn {
  const navigate = useNavigate();
  const { userId } = useParams();

  const summonAurelius = useCallback(
    (entities: SummonEntity) => {
      const limitedKeys = entities.keys.slice(0, 10);
      const limitedNames = entities.displayNames.slice(0, 10);

      const message = buildSummonMessage(
        entities.type,
        limitedKeys,
        limitedNames
      );

      const params = new URLSearchParams({
        tab: 'ai-assistant',
        summonMessage: message,
        summonMode: 'agent',
      });

      navigate(
        `/${userId}/${AuthenticatedRoute.INSIGHTS}?${params.toString()}`
      );
    },
    [navigate, userId]
  );

  return { summonAurelius };
}

/**
 * Build Marcus AI summon message from entity information
 *
 * @param type - Entity type
 * @param keys - Entity keys (up to 10)
 * @param displayNames - Display names (up to 10)
 * @returns Formatted message for Marcus AI
 *
 * @internal
 */
function buildSummonMessage(
  type: 'asset' | 'vulnerability' | 'technology' | 'seed',
  keys: string[],
  displayNames: string[]
): string {
  const pluralMap: Record<string, string> = {
    asset: 'assets',
    vulnerability: 'vulnerabilities',
    technology: 'technologies',
    seed: 'seeds',
  };
  const entityLabel = pluralMap[type];
  const count = keys.length;

  if (count === 1) {
    return `Analyze this ${type}: ${displayNames[0]} (key: ${keys[0]})`;
  }

  const nameList = displayNames
    .map((name, i) => `- ${name} (key: ${keys[i]})`)
    .join('\n');
  return `Analyze these ${count} ${entityLabel}:\n${nameList}`;
}
